import { effectiveAspect, setFrameOrientation } from '../vision/cameraConfig'

// Normalises the raw phone camera stream for display + detection: the phone
// sends its native (usually portrait) frame; this draws it onto a canvas shaped
// to the chosen ratio (16:9 / 4:3 / 1:1) and orientation, rotating -90° when the
// frame's orientation doesn't match the target. The canvas is exposed as a
// MediaStream (captureStream), so every consumer sees a correctly-oriented,
// correctly-proportioned feed — and overlays line up because detection reads
// the same normalised frame.
let video = null
let canvas = null
let ctx = null
let derived = null
let raf = null

const stopLoop = () => {
    if (raf) cancelAnimationFrame(raf)
    raf = null
}

export function buildMobileView(rawStream) {
    destroyMobileView()
    if (!rawStream) return null

    video = document.createElement('video')
    video.srcObject = rawStream
    video.muted = true
    video.playsInline = true
    video.autoplay = true
    video.play().catch(() => {})

    canvas = document.createElement('canvas')
    canvas.width = 1280
    canvas.height = 720
    ctx = canvas.getContext('2d')
    derived = canvas.captureStream(30)

    const draw = () => {
        raf = requestAnimationFrame(draw)
        if (!video || video.readyState < 2) return
        const vw = video.videoWidth
        const vh = video.videoHeight
        if (!vw || !vh) return

        // Auto-orient: report the incoming frame's own orientation (cheap — it
        // no-ops unless it changed and the phone hasn't reported its own).
        setFrameOrientation(vw >= vh ? 'landscape' : 'portrait')

        const aspect = effectiveAspect() // width / height (>=1 = landscape)
        const landscape = aspect >= 1
        const base = 1280
        const W = landscape ? base : Math.round(base * aspect)
        const H = landscape ? Math.round(base / aspect) : base
        if (canvas.width !== W) canvas.width = W
        if (canvas.height !== H) canvas.height = H

        // Rotate a quarter-turn when the phone frame's orientation differs from
        // the target (e.g. portrait phone -> landscape box).
        const frameLandscape = vw >= vh
        const rotate = landscape !== frameLandscape

        ctx.clearRect(0, 0, W, H)
        ctx.save()
        ctx.translate(W / 2, H / 2)
        if (rotate) ctx.rotate(-Math.PI / 2)
        // In the (possibly rotated) drawing space, cover the target box.
        const dw = rotate ? H : W
        const dh = rotate ? W : H
        const scale = Math.max(dw / vw, dh / vh)
        const sw = vw * scale
        const sh = vh * scale
        ctx.drawImage(video, -sw / 2, -sh / 2, sw, sh)
        ctx.restore()
    }
    raf = requestAnimationFrame(draw)
    return derived
}

export function getMobileView() {
    return derived
}

export function destroyMobileView() {
    stopLoop()
    if (derived) {
        try {
            derived.getTracks().forEach((t) => t.stop())
        } catch (e) {
            /* ignore */
        }
    }
    derived = null
    if (video) {
        try {
            video.srcObject = null
        } catch (e) {
            /* ignore */
        }
    }
    video = null
    canvas = null
    ctx = null
}
